import type { MagiMethod } from "./config";

export type MethodSpec = {
  id: MagiMethod;
  icon: string;
  label: string;
  tagline: string;
  description: string;
  requiresOptions?: boolean;
  usesThreshold?: boolean;
  usesAbstain?: boolean;
};

export const METHODS: MethodSpec[] = [
  {
    id: "VoteYesNo",
    icon: "⚖",
    label: "Vote · Yes/No",
    tagline: "Democratic yes / no / abstain verdict",
    description:
      "Each model casts a Yes, No, or Abstain vote with a confidence score. The winning side must exceed the vote threshold, otherwise the system declares no majority.",
    usesThreshold: true,
    usesAbstain: true,
  },
  {
    id: "VoteOptions",
    icon: "☰",
    label: "Vote · Options",
    tagline: "Pick a winner from a custom choice list",
    description:
      "Models choose one of the options you supply. The option with the highest share of votes wins if it clears the threshold.",
    requiresOptions: true,
    usesThreshold: true,
  },
  {
    id: "Probability",
    icon: "ℙ",
    label: "Probability",
    tagline: "Estimate likelihood (0–1)",
    description:
      "Each model returns a probability estimate. The system reports average, median, and range. The median-confidence model writes the analysis.",
    usesAbstain: true,
  },
  {
    id: "Synthesis",
    icon: "⋈",
    label: "Synthesis",
    tagline: "Weave every perspective into one narrative",
    description:
      "Weaves every model's argument, nuance, and point of disagreement into one coherent narrative — preserving the full range of perspectives rather than collapsing them into a single dominant or shared view.",
  },
  {
    id: "Majority",
    icon: "▲",
    label: "Majority",
    tagline: "Amplify the prevailing view",
    description:
      "The highest-confidence model writes a summary of the dominant opinion across all responses. Good for capturing the 'center of gravity' of a discussion.",
  },
  {
    id: "Consensus",
    icon: "∩",
    label: "Consensus",
    tagline: "Find shared common ground",
    description:
      "Identifies overlap across all responses. The output emphasises what the models agree on rather than where they diverge.",
  },
  {
    id: "Minority",
    icon: "▽",
    label: "Minority",
    tagline: "Surface dissent and overlooked angles",
    description:
      "Deliberately elevates the least-confident / minority perspective. Useful for stress-testing a popular conclusion or finding blind spots.",
  },
  {
    id: "Compose",
    icon: "✎",
    label: "Compose",
    tagline: "Generate and peer-review content",
    description:
      "Round 1: each model drafts a response. Round 2: models blindly rate each peer's draft. Returns a ranked list with peer justifications.",
  },
];

export const METHODS_BY_ID: Record<MagiMethod, MethodSpec> = Object.fromEntries(
  METHODS.map((m) => [m.id, m]),
) as Record<MagiMethod, MethodSpec>;

export function methodRequiresOptions(m: MagiMethod): boolean {
  return METHODS_BY_ID[m]?.requiresOptions === true;
}
